import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Autocomplete,
  Box,
  FormHelperText,
} from '@mui/material';
import 'react-phone-input-2/lib/material.css'; // or style.css or bootstrap.css
import PhoneInput from 'react-phone-input-2';
import axios from 'axios';
import { ThemeProvider } from '@mui/material/styles';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, clearDraft } from '../../utils/draftUtils';
import { API_BASE_URL } from '../../lib/constants';
import theme from '../../theme/theme.js';
import ShareIcon from '@mui/icons-material/Share';
import IconButton from '@mui/material/IconButton';

import { useNotification } from '../../contexts/NotificationContext';

const ConsiderationForm = () => {
  const { showNotification } = useNotification();
  const { uhid_no } = useParams(); // Assuming you're using route params for UHID
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [patient, setPatient] = useState({ uhid_no: '', name: '' });
  const [success, setSuccess] = useState(false);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case 'date_of_birth':
        if (!value) {
          error = 'Date of Birth is required';
        } else {
          const selectedDate = new Date(value);
          const today = new Date();
          selectedDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          if (selectedDate >= today) {
            error = 'DOB must be before today';
          }
        }
        break;
      case 'emergency_contact':
        if (!value || (typeof value === 'string' && value.trim() === '')) error = 'Emergency Contact is required';
        break;
      case 'existing_medical_conditions':
        if (!value || !value.trim()) error = 'Existing Medical Conditions is required';
        break;
      case 'surgery_type':
        if (!value || !value.trim()) error = 'Surgery Type is required';
        break;
      case 'benefits_of_surgery':
        if (!value || !value.trim()) error = 'Benefits of Surgery is required';
        break;
      case 'risks_of_surgery':
        if (!value || !value.trim()) error = 'Risks of Surgery is required';
        break;
      case 'alternatives_to_surgery':
        if (!value || !value.trim()) error = 'Alternatives to Surgery is required';
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value);
  };

  const [formData, setFormData] = useState({
    uhid_no: '',
    date_of_birth: '',
    emergency_contact: '',
    existing_medical_conditions: '',
    past_surgeries: '',
    current_medications: '',
    fasting_acknowledgment: false,
    medication_adjustment_acknowledgment: false,
    benefits_of_surgery: '',
    risks_of_surgery: '',
    alternatives_to_surgery: '',
    pre_surgical_tests: {
      cbc: false,
      blood_sugar: false,
      liver_tests: false,
      kidney_tests: false,
      xray: false,
      ecg: false,
      ultrasound: false,
      urine_test: false,
      covid_test: false,
    },
    planned_surgical_date: '',
    surgery_type: '',
    planned_anesthesia_type: '',
    consent_surgery: false,
    consent_blood_transfusion: false,
    consent_anesthesia: false,
  });


  const navigate = useNavigate();


  // fetchConsiderationData must be defined before useEffect uses it
  const fetchConsiderationData = useCallback(async (uhidNo) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get_consideration_phase/${uhidNo}`);
      if (response.data) {
        const data = response.data;
        const formattedDOB = data.date_of_birth
          ? new Date(data.date_of_birth).toISOString().slice(0, 10)
          : '';

        const formattedSurgicalDate = data.planned_surgical_date
          ? new Date(data.planned_surgical_date).toISOString().slice(0, 10)
          : '';

        // Default pre_surgical_tests structure
        const defaultPreSurgicalTests = {
          cbc: false,
          blood_sugar: false,
          liver_tests: false,
          kidney_tests: false,
          xray: false,
          ecg: false,
          ultrasound: false,
          urine_test: false,
          covid_test: false,
        };

        // Normalize pre_surgical_tests from API: it may come as an array of selected keys
        let preSurgicalTests = defaultPreSurgicalTests;
        if (Array.isArray(data.pre_surgical_tests)) {
          // Convert array to boolean map
          const base = { ...defaultPreSurgicalTests };
          Object.keys(base).forEach(k => { base[k] = false; });
          data.pre_surgical_tests.forEach(k => {
            if (k in base) base[k] = true;
          });
          preSurgicalTests = base;
        } else if (typeof data.pre_surgical_tests === 'object' && data.pre_surgical_tests !== null) {
          preSurgicalTests = { ...defaultPreSurgicalTests, ...data.pre_surgical_tests };
        }

        // Update form data with fetched data
        setFormData(prev => ({
          ...prev,
          ...data,
          date_of_birth: formattedDOB,
          planned_surgical_date: formattedSurgicalDate,
          pre_surgical_tests: preSurgicalTests,
          fasting_acknowledgment: data.fasting_acknowledgment,
          medication_adjustment_acknowledgment: data.medication_adjustment_acknowledgment,
          consent_surgery: data.consent_surgery,
          consent_blood_transfusion: data.consent_blood_transfusion,
          consent_anesthesia: data.consent_anesthesia,
        }));

        setSuccess(true);
        setDataLoaded(true); // Set data loaded to true after successful fetch
      }
    } catch (err) {
      console.log("No existing consideration form found, continuing with blank.");
      setDataLoaded(true); // Set data loaded even if no data found
    }
  }, []);

  // Fetch patient data on component mount
  useEffect(() => {
    if (uhid_no) {
      fetchPatientData(uhid_no);
      fetchConsiderationData(uhid_no);
      // Load draft if exists
      const draft = loadDraft(DRAFT_KEYS.CONSIDERATION_PHASE, uhid_no);
      if (draft && draft.formData) {
        setFormData(draft.formData);
        console.log('Draft loaded for Consideration Phase');
      }
      // If no draft and no DB data, clear local/session storage
      const clearIfNoData = async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/get_consideration_phase/${uhid_no}`);
          if (!res.data || Object.keys(res.data).length === 0) {
            sessionStorage.removeItem(`consideration_form_${uhid_no}`);
            sessionStorage.removeItem(`formStatus_consideration_${uhid_no}`);
            if (typeof clearDraft === 'function') clearDraft(DRAFT_KEYS.CONSIDERATION_PHASE, uhid_no);
          }
        } catch {
          // On error (e.g. 404), also clear
          sessionStorage.removeItem(`consideration_form_${uhid_no}`);
          sessionStorage.removeItem(`formStatus_consideration_${uhid_no}`);
          if (typeof clearDraft === 'function') clearDraft(DRAFT_KEYS.CONSIDERATION_PHASE, uhid_no);
        }
      };
      clearIfNoData();
    }
  }, [uhid_no]);

  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded and not in update mode
  useEffect(() => {
    if (!dataLoaded || success) return; // Don't auto-save until initial data is loaded or if in update mode

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded, success, uhid_no]);

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`consideration_form_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`consideration_form_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setFormData(saved);
      setLoading(false);
      setSuccess(false);
    }
    // Always enable buttons after draft load
    // eslint-disable-next-line
  }, []);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    sessionStorage.removeItem(`consideration_form_${uhid_no}`);
    sessionStorage.removeItem(`formStatus_consideration_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(formData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_consideration_${uhid_no}`, JSON.stringify({
        formName: 'ConsiderationForm ',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/considerationPhase/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);


      const response = await axios.get(`${API_BASE_URL}/get_patient_consideration/${uhidNo}`);
      const data = response.data;
      const formattedDOB = data.date_of_birth
        ? new Date(data.date_of_birth).toISOString().slice(0, 10)
        : '';

      setPatient(response.data);
      console.log(response.data);
      setFormData(prev => ({
        ...prev,
        uhid_no: response.data.uhid_no,
        date_of_birth: formattedDOB,


      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setError('Failed to fetch patient data. Please try again.');
      setLoading(false);
    }
  };




  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value,
        },
      }));
    } else if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    if (!validate()) {
      showNotification('error', "Please fill all required fields.");
      setLoading(false);
      return;
    }

    const selectedTests = Object.keys(formData.pre_surgical_tests)
      .filter(test => formData.pre_surgical_tests[test])
      .map(test => test);

    const submitData = {
      hospital_id: 1,
      uhid_no: formData.uhid_no,
      date_of_birth: formData.date_of_birth,
      emergency_contact: formData.emergency_contact,
      existing_medical_conditions: formData.existing_medical_conditions,
      past_surgeries: formData.past_surgeries,
      pre_surgical_tests: selectedTests,
      current_medications: formData.current_medications,
      benefits_of_surgery: formData.benefits_of_surgery,
      risks_of_surgery: formData.risks_of_surgery,
      alternatives_to_surgery: formData.alternatives_to_surgery,
      planned_surgical_date: formData.planned_surgical_date,
      surgery_type: formData.surgery_type,
      fasting_acknowledgment: formData.fasting_acknowledgment ? 1 : 0,
      medication_adjustment_acknowledgment: formData.medication_adjustment_acknowledgment ? 1 : 0,
      consent_surgery: formData.consent_surgery ? 1 : 0,
      consent_blood_transfusion: formData.consent_blood_transfusion ? 1 : 0,
      consent_anesthesia: formData.consent_anesthesia ? 1 : 0,
    };

    try {
      if (success) {
        await axios.put(`${API_BASE_URL}/update_consideration_phase/${uhid_no}`, submitData,
          { withCredentials: true });
        showNotification('success', "Consideration Phase Form updated successfully!");
        setSuccess(true);
        setError("");
        // Show update message, then navigate
        setTimeout(() => {
          clearSavedData();
          clearDraft(DRAFT_KEYS.CONSIDERATION_PHASE, uhid_no);
          navigate(`/mainPage/${formData.uhid_no}`);
        }, 1200);
      } else {
        const response = await axios.post(`${API_BASE_URL}/consideration_phase`, submitData,
          { withCredentials: true });
        console.log('Form Submitted: ', response.data);
        showNotification('success', 'Consideration Phase Form submitted successfully!');
        setSuccess(true);
        setError("");
        // Show submit message, then navigate
        setTimeout(() => {
          clearSavedData();
          clearDraft(DRAFT_KEYS.CONSIDERATION_PHASE, uhid_no);
          navigate(`/mainPage/${formData.uhid_no}`);
        }, 1200);
      }
    } catch (error) {
      console.error('There was an error submitting the form!', error);
      showNotification('error', `Error: ${error.message}`);
      setError('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const medicalConditions = [
    'Diabetes',
    'Hypertension',
    'Asthma',
    'Heart Disease',
    'Thyroid Disorder',
    'Arthritis',
    'Depression',
    'Anxiety',
    'Allergies',
    'Epilepsy',
    'Chronic Back Pain',
    'Cancer',
    'None',
  ];
  const surgeryOptions = [
    "Laparoscopic Appendectomy",
    "Inguinal Hernia Repair",
    "Cesarean Section (C-section)",
    "Gallbladder Removal (Cholecystectomy)",
    "Knee Replacement",
    "Coronary Artery Bypass",
    "Hip Replacement",
    "Tonsillectomy",
    "Hysterectomy",
    "Cataract Surgery",
  ];
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const validate = () => {
    let tempErrors = {};

    // Date of Birth validation
    if (!formData.date_of_birth) {
      tempErrors.date_of_birth = 'Date of Birth is required';
    } else {
      const selectedDate = new Date(formData.date_of_birth);
      const today = new Date();

      // Set time to 00:00:00 to ignore time comparison
      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (selectedDate >= today) {
        tempErrors.date_of_birth = 'DOB must be before today';
      }
    }

    // Emergency Contact validation: required
    if (!formData.emergency_contact || (typeof formData.emergency_contact === 'string' && formData.emergency_contact.trim() === '')) {
      tempErrors.emergency_contact = 'Emergency Contact is required';
    }

    if (!formData.existing_medical_conditions.trim()) {
      tempErrors.existing_medical_conditions = 'Existing Medical Conditions is required';
    }

    if (!formData.surgery_type.trim()) {
      tempErrors.surgery_type = 'Surgery Type is required';
    }

    if (!formData.fasting_acknowledgment) {
      tempErrors.fasting_acknowledgment = 'Fasting acknowledgment is required';
    }

    if (!formData.medication_adjustment_acknowledgment) {
      tempErrors.medication_adjustment_acknowledgment = 'Medication adjustment acknowledgment is required';
    }

    if (!formData.consent_surgery) {
      tempErrors.consent_surgery = 'Consent for Surgery is required';
    }

    if (!formData.consent_blood_transfusion) {
      tempErrors.consent_blood_transfusion = 'Consent for Blood Transfusion is required';
    }

    if (!formData.consent_anesthesia) {
      tempErrors.consent_anesthesia = 'Consent for Anesthesia is required';
    }

    if (formData.planned_surgical_date && new Date(formData.planned_surgical_date) < new Date(new Date().toDateString())) {
      tempErrors.planned_surgical_date = 'Planned surgical date cannot be in the past';
    }

    if (!formData.benefits_of_surgery.trim()) {
      tempErrors.benefits_of_surgery = 'Benefits of Surgery is required';
    }

    if (!formData.risks_of_surgery.trim()) {
      tempErrors.risks_of_surgery = 'Risks of Surgery is required';
    }

    if (!formData.alternatives_to_surgery.trim()) {
      tempErrors.alternatives_to_surgery = 'Alternatives to Surgery is required';
    }

    setErrors(tempErrors);

    if (Object.keys(tempErrors).length > 0) {
      const firstErrorField = Object.keys(tempErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      showNotification('error', "Please fill in all required fields.");
    }
    return Object.keys(tempErrors).length === 0;
  };

  if (loading && !patient.name) {
    return (
      <Container maxWidth="md" sx={{ marginTop: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  const medicationOptions = [
    'Paracetamol',
    'Metformin',
    'Amlodipine',
    'Telmisartan',
    'Insulin',
    'Thyronorm',
    'Aspirin',
    'Clopidogrel',
    'Vitamin D3',
    'Calcium',
    'Ibuprofen',
    'Rantac',
    'Gelusil',
    'Salbutamol Inhaler',
    'None'
  ];
  const benefitsOptions = [
    "Relieves pain and improves quality of life.",
    "Removes source of infection or disease.",
    "Restores normal function and mobility.",
    "Improves long-term prognosis and prevents complications."
  ];

  const risksOptions = [
    "Infection, bleeding, or reaction to anesthesia.",
    "Damage to nearby organs or tissues.",
    "Post-operative pain or delayed healing.",
    "Scarring or need for additional procedures."
  ];

  const alternativesOptions = [
    "Medical management with medication.",
    "Physical therapy or lifestyle changes.",
    "Watchful waiting with regular monitoring.",
    "No treatment at this time."
  ];
  const pastSurgeriesOptions = [
    'Appendectomy',
    'Cholecystectomy',
    'Cesarean Section',
    'Coronary Artery Bypass',
    'Hysterectomy',
    'Knee Replacement',
  ];
  // (Moved useState for submitStatus to the top to comply with React Hooks rules)

  const handleShareClick = async () => {
    try {
      // Call API to generate a share token for the given UHID
      const res = await axios.post(`${API_BASE_URL}/generate_for_consideration_api_token/${uhid_no}`);

      const token = res.data.token;
      const link = `${API_BASE_URL}/considerationPhase/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,

Please fill the post-operative analgesia chart:

${link}`;

      // Copy the shareable link to clipboard
      await navigator.clipboard.writeText(link);

      // Show success notification to user
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      // Open WhatsApp chat with pre-filled message (replace phone number if needed)
      const phoneNumber = '+916379605575'; // no spaces or special chars for wa.me
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);

      // Show error notification to user
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };
  return (

    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <button
          onClick={() => navigate(`/mainPage/${formData.uhid_no}`)}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#008080',
            fontSize: '16px',
            fontWeight: 500,
            cursor: 'pointer',
            marginRight: '16px',
          }}
        >
          ← Back
        </button>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              CONSIDERATION PHASE
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>
          <br></br>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            {/* Left-aligned Patient Info */}
            <Typography variant="body1" sx={{ color: '#008080' }}>
              <b>UHID:</b> {patient.uhid_no} &nbsp; &nbsp; <b>Patient Name:</b> {patient.name}
            </Typography>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>

          <br></br>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Patient Information */}
              <Grid item xs={12}>
                <TextField
                  label="UHID NO"
                  name="uhid_no"
                  value={formData.uhid_no}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true, }}

                />
              </Grid>
              <Grid item xs={12} >
                <TextField
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true, }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: new Date().toISOString().split("T")[0], // Prevents future dates
                  }}
                  error={Boolean(errors.date_of_birth)}
                  helperText={errors.date_of_birth}
                />

              </Grid>

              <Grid item xs={12} >
                <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                  Emergency Contact Number <span style={{ color: 'red' }}>*</span>
                </Typography>
                <PhoneInput
                  country={'in'}
                  value={formData.emergency_contact || ''}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, emergency_contact: value }))
                  }
                  enableSearch
                  inputProps={{
                    name: 'emergency_contact',
                    required: true,
                  }}
                  containerStyle={{ width: '100%', marginTop: '16px' }}
                  inputStyle={{ width: '100%', height: '56px' }}
                  buttonStyle={{ height: '56px' }}
                  helperText={errors.emergency_contact ? 'Required' : ''}
                  error={Boolean(errors.emergency_contact)}
                />
                <div
                  style={{
                    color: errors.emergency_contact ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                    marginLeft: '14px',
                  }}
                >
                  {errors.emergency_contact || ' '}
                </div>

              </Grid>

              {/* Medical History & Risk Assessment */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={medicalConditions}
                  value={formData.existing_medical_conditions}
                  onInputChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      existing_medical_conditions: newValue,
                    }));
                    if (errors.existing_medical_conditions) {
                      setErrors((prev) => ({ ...prev, existing_medical_conditions: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Existing Medical Conditions"
                      name="existing_medical_conditions"
                      onBlur={handleBlur}
                      required
                      fullWidth
                      error={Boolean(errors.existing_medical_conditions)}
                      helperText={errors.existing_medical_conditions}
                    />
                  )}
                />

              </Grid>

              <Grid item xs={12} sm={6}>

                <Autocomplete
                  freeSolo
                  options={pastSurgeriesOptions}
                  value={formData.past_surgeries}
                  onInputChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      past_surgeries: newValue,
                    }));
                    if (errors.past_surgeries) {
                      setErrors((prev) => ({ ...prev, past_surgeries: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Past Surgeries"
                      name="past_surgeries"
                      onBlur={handleBlur}
                      fullWidth
                      error={Boolean(errors.past_surgeries)}
                      helperText={errors.past_surgeries}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={medicationOptions}
                  value={formData.current_medications}
                  onInputChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      current_medications: newValue,
                    }));
                    if (errors.current_medications) {
                      setErrors((prev) => ({ ...prev, current_medications: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Current Medications"
                      name="current_medications"
                      onBlur={handleBlur}
                      fullWidth
                      error={Boolean(errors.current_medications)}
                      helperText={errors.current_medications}
                    />
                  )}
                />

              </Grid>

              {/* Add Fasting and Medication Adjustment Instructions */}
              <Grid item xs={12}>
                <Typography variant="h6">Important Pre-Surgery Instructions</Typography>
                <Typography variant="body1">
                  <strong>No food or drink for 6-12 hours before the operation (as per guidelines).</strong>
                  <br />
                  <strong>Patients with chronic conditions (diabetes, hypertension) need medication adjustments.</strong>
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.fasting_acknowledgment} onChange={handleChange} name="fasting_acknowledgment" />}
                  label={<span>I acknowledge the fasting requirement before surgery.<span style={{ color: 'red' }}>*</span></span>}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.medication_adjustment_acknowledgment} onChange={handleChange} name="medication_adjustment_acknowledgment" />}

                  label={<span>I acknowledge that medication adjustments may be required for chronic conditions. <span style={{ color: 'red' }}>*</span></span>}
                />
              </Grid>

              {/* Benefits, Risks, and Alternatives of Surgery */}
              <Grid item xs={12}>
                <Typography variant="h6">Benefits, Risks, and Alternatives</Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={benefitsOptions}
                  value={formData.benefits_of_surgery}
                  onInputChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      benefits_of_surgery: newValue,
                    }));
                    if (errors.benefits_of_surgery) {
                      setErrors((prev) => ({ ...prev, benefits_of_surgery: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Benefits of Surgery"
                      name="benefits_of_surgery"
                      onBlur={handleBlur}
                      fullWidth
                      multiline
                      required
                      rows={3}
                      error={Boolean(errors.benefits_of_surgery)}
                      helperText={errors.benefits_of_surgery}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={risksOptions}
                  value={formData.risks_of_surgery}
                  onInputChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      risks_of_surgery: newValue,
                    }));
                    if (errors.risks_of_surgery) {
                      setErrors((prev) => ({ ...prev, risks_of_surgery: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Risks of Surgery"
                      name="risks_of_surgery"
                      onBlur={handleBlur}
                      fullWidth
                      required
                      multiline
                      rows={3}
                      error={Boolean(errors.risks_of_surgery)}
                      helperText={errors.risks_of_surgery}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={alternativesOptions}
                  value={formData.alternatives_to_surgery}
                  onInputChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      alternatives_to_surgery: newValue,
                    }));
                    if (errors.alternatives_to_surgery) {
                      setErrors((prev) => ({ ...prev, alternatives_to_surgery: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Alternatives to Surgery"
                      name="alternatives_to_surgery"
                      onBlur={handleBlur}
                      fullWidth
                      required
                      multiline
                      rows={3}
                      error={Boolean(errors.alternatives_to_surgery)}
                      helperText={errors.alternatives_to_surgery}
                    />
                  )}
                />

              </Grid>

              {/* Pre-Surgical Tests */}
              <Grid item xs={12}>
                <Typography variant="h6">Pre-Surgical Tests</Typography>
              </Grid>
              {Object.keys(formData.pre_surgical_tests).map((test) => (
                <Grid item xs={12} sm={6} key={test}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.pre_surgical_tests[test]}
                        onChange={handleChange}
                        name={`pre_surgical_tests.${test}`}
                      />
                    }
                    label={test.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                  />
                </Grid>
              ))}

              {/* Surgical Risk Assessment */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={surgeryOptions}
                  value={formData.surgery_type}
                  onInputChange={(event, newInputValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      surgery_type: newInputValue,
                    }));
                    if (errors.surgery_type) {
                      setErrors((prev) => ({ ...prev, surgery_type: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Surgery Type"
                      name="surgery_type"
                      onBlur={handleBlur}
                      required
                      fullWidth
                      error={Boolean(errors.surgery_type)}
                      helperText={errors.surgery_type}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Planned Surgical Date"
                  name="planned_surgical_date"
                  type="date"
                  value={formData.planned_surgical_date}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.planned_surgical_date}
                  helperText={errors.planned_surgical_date || ' '}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{ min: new Date().toISOString().slice(0, 10) }} // only date, no time
                />

              </Grid>

              {/* Informed Consent */}
              <Grid item xs={12}>
                <Typography variant="h6">Informed Consent</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.consent_surgery}
                      onChange={handleChange}
                      name="consent_surgery"
                    />
                  }
                  label={<span>Consent for Surgery <span style={{ color: 'red' }}>*</span></span>} />
                {errors.consent_surgery && <Typography variant="body2" color="error">Please confirm your consent for surgery.</Typography>}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.consent_blood_transfusion}
                      onChange={handleChange}
                      name="consent_blood_transfusion"
                    />
                  }
                  label={<span>Consent for Blood Transfusion <span style={{ color: 'red' }}>*</span></span>} />
                {errors.consent_blood_transfusion && <Typography variant="body2" color="error">Please confirm your consent for blood transfusion.</Typography>}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.consent_anesthesia}
                      onChange={handleChange}
                      name="consent_anesthesia"
                    />
                  }
                  label={<span>Consent for Anesthesia <span style={{ color: 'red' }}>*</span></span>} />
                {errors.consent_anesthesia && <Typography variant="body2" color="error">Please confirm your consent for anesthesia.</Typography>}
              </Grid>

              {/* Submit Button */}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <DraftSaveButton
                    formKey={DRAFT_KEYS.CONSIDERATION_PHASE}
                    uhidNo={uhid_no}
                    formData={formData}
                    onDraftLoaded={(draftData) => {
                      setFormData(draftData);
                      setLoading(false);
                      setSuccess(false);
                    }}
                    variant="outlined"
                    size="large"

                    hidden={success}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading}
                  >
                    {success ? 'Update' : 'Submit'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ConsiderationForm;